import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, CalendarCheck, IndianRupee, Percent } from "lucide-react";
import api, { formatINR, formatINRShort } from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import { Skeleton } from "@/components/ui/skeleton";

const Stat = ({ icon: Icon, label, value, sub, dark }) => (
  <div className={`rounded-xl border p-5 shadow-pulse-sm ${dark ? "border-transparent bg-pulse-navy text-white grain-overlay" : "border-slate-200 bg-white"}`}>
    <div className="relative z-10 flex items-center justify-between">
      <span className={`text-xs font-medium uppercase tracking-wider ${dark ? "text-white/60" : "text-slate"}`}>{label}</span>
      <Icon size={16} className="text-pulse-green" />
    </div>
    <div className={`relative z-10 mt-2 text-2xl font-bold ${dark ? "text-white" : "text-pulse-navy"}`}>{value}</div>
    {sub && <div className={`relative z-10 mt-0.5 text-xs ${dark ? "text-white/50" : "text-slate"}`}>{sub}</div>}
  </div>
);

export default function AnalyticsView() {
  const { businessId } = useDash();
  const [period, setPeriod] = useState("month");
  const [itemCat, setItemCat] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["analytics", businessId, period],
    queryFn: async () => (await api.get("/analytics", { params: { business_id: businessId, period } })).data,
    enabled: !!businessId,
  });

  if (isLoading || !data) return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({length:4}).map((_,i)=><Skeleton key={i} className="h-28 w-full rounded-xl" />)}</div>
      <Skeleton className="h-80 w-full rounded-xl" />
    </div>
  );

  const periodLabel = period === "month" ? "this month" : data.fy_label;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-pulse-navy">Analytics</h1>
          <p className="text-sm text-slate">Revenue, occupancy and booker insights.</p>
        </div>
        <div className="flex gap-1 rounded-md border border-slate-200 bg-white p-1" data-testid="analytics-period-toggle">
          {[["month","This month"],["fy",data.fy_label]].map(([v,l]) => (
            <button key={v} onClick={() => setPeriod(v)} data-testid={`period-${v}`}
              className={`rounded px-4 py-1.5 text-sm font-medium transition-colors ${period === v ? "bg-pulse-navy text-white" : "text-slate hover:text-pulse-navy"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={IndianRupee} label={`Revenue ${periodLabel}`} value={formatINR(data.revenue)} dark />
        <Stat icon={CalendarCheck} label={`Bookings ${periodLabel}`} value={data.bookings} />
        <Stat icon={TrendingUp} label="Avg booking value" value={formatINR(data.avg_value)} />
        <Stat icon={Percent} label="Top venue occupancy" value={`${Math.max(0, ...data.by_venue.map(v=>v.occupancy)).toFixed(0)}%`} sub="of days booked" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Revenue by venue */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm">
          <h2 className="text-lg font-semibold text-pulse-navy">Revenue by venue</h2>
          <div className="mt-4 h-64 min-h-[256px]" data-testid="chart-revenue-venue">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_venue} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={formatINRShort} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 13 }} />
                <Bar dataKey="revenue" radius={[8, 8, 0, 0]}>
                  {data.by_venue.map((v) => <Cell key={v.name} fill={v.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bookings distribution */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm">
          <h2 className="text-lg font-semibold text-pulse-navy">Bookings distribution</h2>
          <div className="mt-4 h-64 min-h-[256px]" data-testid="chart-distribution">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.distribution.filter(d=>d.value>0)} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {data.distribution.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {data.distribution.map((d) => (
              <span key={d.name} className="flex items-center gap-1.5 text-xs text-slate"><span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />{d.name} ({d.value})</span>
            ))}
          </div>
        </div>
      </div>

      {/* Add-on items by category */}
      {(data.items_by_category?.length > 0) && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm" data-testid="analytics-items">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-pulse-navy">Add-on items · {periodLabel}</h2>
            <div className="flex flex-wrap gap-1.5" data-testid="item-cat-filter">
              {["all", ...(data.item_categories || [])].map((c) => (
                <button key={c} onClick={() => setItemCat(c)} data-testid={`item-cat-${c}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${itemCat === c ? "border-pulse-navy bg-pulse-navy text-white" : "border-slate-200 text-slate hover:bg-cloud"}`}>
                  {c === "all" ? "All" : c}
                </button>
              ))}
            </div>
          </div>
          {(() => {
            const rows = data.items_by_category.filter((r) => itemCat === "all" || r.category === itemCat);
            const tRev = rows.reduce((a, r) => a + r.revenue, 0);
            const tCost = rows.reduce((a, r) => a + r.cost, 0);
            return (
              <>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-lg bg-cloud p-4"><div className="text-xs uppercase tracking-wide text-slate">Item revenue</div><div className="mt-1 text-xl font-bold text-pulse-navy" data-testid="items-revenue">{formatINR(tRev)}</div></div>
                  <div className="rounded-lg bg-cloud p-4"><div className="text-xs uppercase tracking-wide text-slate">Item cost (to expenses)</div><div className="mt-1 text-xl font-bold text-red-600" data-testid="items-cost">{formatINR(tCost)}</div></div>
                  <div className="rounded-lg bg-cloud p-4"><div className="text-xs uppercase tracking-wide text-slate">Item profit</div><div className="mt-1 text-xl font-bold text-pulse-green" data-testid="items-profit">{formatINR(tRev - tCost)}</div></div>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-y border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate">
                      <th className="px-3 py-2">Category</th><th className="px-3 py-2 text-right">Revenue</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Profit</th><th className="px-3 py-2 text-right">Count</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {rows.map((r) => (
                        <tr key={r.category} data-testid={`item-row-${r.category}`}>
                          <td className="px-3 py-2 font-medium text-charcoal">{r.category}</td>
                          <td className="px-3 py-2 text-right text-pulse-navy">{formatINR(r.revenue)}</td>
                          <td className="px-3 py-2 text-right text-red-600">{formatINR(r.cost)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-pulse-green">{formatINR(r.profit)}</td>
                          <td className="px-3 py-2 text-right text-slate">{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-xs text-slate">Item revenue is included in your total revenue; item cost is deducted in Profit &amp; Loss.</p>
              </>
            );
          })()}
        </div>
      )}

      {/* Profit & Loss */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm" data-testid="analytics-pnl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-pulse-navy">Profit &amp; Loss · {periodLabel}</h2>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
            <span className="text-slate">Revenue <b className="text-pulse-navy">{formatINR(data.revenue)}</b></span>
            <span className="text-slate">Expenses <b className="text-red-600">{formatINR(data.total_expenses)}</b></span>
            <span className="text-slate">Net profit <b className={data.total_profit >= 0 ? "text-pulse-green" : "text-red-600"}>{formatINR(data.total_profit)}</b> <span className="text-xs text-slate">({data.margin}% margin)</span></span>
          </div>
        </div>
        {!data.has_expenses ? (
          <p className="mt-4 rounded-md bg-cloud px-4 py-3 text-sm text-slate">Add expenses under the Calendar's <b>Profit &amp; Loss</b> section to see your real net profit per venue.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="pnl-table">
              <thead><tr className="border-y border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate">
                <th className="px-3 py-2">Venue</th><th className="px-3 py-2">Revenue</th><th className="px-3 py-2">Expenses</th><th className="px-3 py-2">Profit</th><th className="px-3 py-2">Margin</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {data.by_venue.map((v) => (
                  <tr key={v.name}>
                    <td className="px-3 py-2 font-medium text-charcoal"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: v.color }} />{v.name}</td>
                    <td className="px-3 py-2 text-slate">{formatINR(v.revenue)}</td>
                    <td className="px-3 py-2 text-red-600">{formatINR(v.expenses)}</td>
                    <td className={`px-3 py-2 font-semibold ${v.profit >= 0 ? "text-pulse-green" : "text-red-600"}`}>{formatINR(v.profit)}</td>
                    <td className="px-3 py-2 text-slate">{v.revenue ? Math.round((v.profit / v.revenue) * 100) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Monthly trend */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm">
        <h2 className="text-lg font-semibold text-pulse-navy">Revenue &amp; profit trend · {data.fy_label}</h2>
        <div className="mt-4 h-64 min-h-[256px]" data-testid="chart-trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.trend} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={formatINRShort} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" name="Revenue" dataKey="revenue" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3, fill: "#10B981" }} activeDot={{ r: 5 }} />
              <Line type="monotone" name="Profit" dataKey="profit" stroke="#0A192F" strokeWidth={2.5} dot={{ r: 3, fill: "#0A192F" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Occupancy */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm">
        <h2 className="text-lg font-semibold text-pulse-navy">Occupancy · {periodLabel}</h2>
        <div className="mt-4 space-y-4" data-testid="occupancy-list">
          {data.by_venue.map((v) => (
            <div key={v.name}>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 font-medium text-charcoal"><span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />{v.name}</span>
                <span className="text-slate">{v.booked_days}/{v.total_days} days · <b className="text-pulse-navy">{v.occupancy}%</b></span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-cloud">
                <div className="h-full rounded-full transition-all" style={{ width: `${v.occupancy}%`, background: v.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bookers table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-pulse-sm">
        <h2 className="p-5 pb-3 text-lg font-semibold text-pulse-navy">Bookers comparison</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bookers-table">
            <thead><tr className="border-y border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate">
              <th className="px-5 py-3">Booker</th>
              <th className="px-5 py-3">{periodLabel} bookings</th>
              <th className="px-5 py-3">{periodLabel} revenue</th>
              <th className="px-5 py-3">All-time bookings</th>
              <th className="px-5 py-3">All-time revenue</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {data.bookers.map((b) => (
                <tr key={b.name} className="hover:bg-cloud/50">
                  <td className="px-5 py-3 font-medium text-charcoal">{b.name}</td>
                  <td className="px-5 py-3 text-slate">{b.period_bookings}</td>
                  <td className="px-5 py-3 text-slate">{formatINR(b.period_revenue)}</td>
                  <td className="px-5 py-3 text-slate">{b.all_bookings}</td>
                  <td className="px-5 py-3 font-semibold text-pulse-navy">{formatINR(b.all_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
